import { selectIElementsSlice } from "@/store/ielements-slice-selector";
import { RootState } from "@/store/store";
import { isAssetDetectionTask } from "@/utils/background-tasks";
import { LabelType, MarkupIElement } from "@faro-lotv/ielement-types";
import { BackgroundTaskState } from "@faro-lotv/project-api-client";
import { IElementsState } from "@faro-lotv/project-source";
import { createSelector } from "@reduxjs/toolkit";
import { selectBackgroundTasks } from "../background-tasks/background-tasks-selector";
import {
  getAiDetectedTag,
  selectAiDetectedMarkupIds,
  selectAiDetectedMarkups,
} from "./ai-asset-review-data-selectors";
import {
  AiAssetReviewState,
  AiAssetReviewStatus,
  AiGeneratedAnnotation,
  AiReviewViewMode,
} from "./ai-asset-review-slice";

type AiAssetReviewStore = {
  aiAssetReview: AiAssetReviewState;
};

/**
 * @param state The current state of the store
 * @returns current view mode
 */
export function selectAiReviewViewMode(
  state: AiAssetReviewStore,
): AiReviewViewMode {
  return state.aiAssetReview.viewMode;
}

function getAreaName(
  markup: MarkupIElement,
  iElementsSlice: IElementsState,
): string | undefined {
  const parent = markup.parentId
    ? iElementsSlice.iElements[markup.parentId]
    : null;
  const grandParent = parent?.parentId
    ? iElementsSlice.iElements[parent.parentId]
    : null;
  const areaElement = grandParent?.parentId
    ? iElementsSlice.iElements[grandParent.parentId]
    : null;
  return areaElement?.name;
}

/**
 * @param markup The markup element to get the thumbnail from
 * @param iElementsSlice The iElements slice to get the thumbnail from
 * @returns the thumbnail or undefined if not found
 */
function getThumbnailFromMarkup(
  markup: MarkupIElement,
  iElementsSlice: IElementsState,
): string | undefined {
  const parent = markup.parentId
    ? iElementsSlice.iElements[markup.parentId]
    : null;
  return parent?.thumbnailUri ?? undefined;
}

/**
 * @param state The current state of the store
 * @returns all annotations
 */
export const selectAiReviewAnnotations = createSelector(
  [selectAiDetectedMarkups, selectIElementsSlice],
  (markups, iElementsSlice): AiGeneratedAnnotation[] =>
    markups.map((markup) => ({
      id: markup.id,
      markup,
      title: markup.name,
      tag: getAiDetectedTag(markup) ?? "",
      area: getAreaName(markup, iElementsSlice),
      thumbnail: getThumbnailFromMarkup(markup, iElementsSlice),
      status: deriveStatusFromLabels(markup),
      reviewNotes:
        typeof markup.metaDataMap?.reviewNotes === "string"
          ? markup.metaDataMap.reviewNotes
          : undefined,
    })),
);

/**
 * Selects the id of the current annotation, if any
 *
 * @param s Root state
 * @returns current annotation id or undefined
 */
function selectCurrentAnnotationId(s: AiAssetReviewStore): string | undefined {
  return s.aiAssetReview.currentAnnotationId;
}

export const selectCurrentAnnotation = createSelector(
  [selectAiReviewAnnotations, selectCurrentAnnotationId],
  (annotations, id): AiGeneratedAnnotation | null =>
    annotations.find((a) => a.id === id) ?? null,
);

export const selectNextAnnotationId = createSelector(
  [selectAiDetectedMarkupIds, selectCurrentAnnotationId],
  (ids, currentId): string | undefined => {
    if (ids.length === 0) return undefined;
    const idx = currentId ? ids.indexOf(currentId) : -1;
    const nextIdx = Math.min(idx + 1, ids.length - 1);
    return ids[nextIdx];
  },
);

export const selectPreviousAnnotationId = createSelector(
  [selectAiDetectedMarkupIds, selectCurrentAnnotationId],
  (ids, currentId): string | undefined => {
    if (ids.length === 0) return undefined;
    const idx = currentId ? ids.indexOf(currentId) : ids.length;
    const prevIdx = Math.max(idx - 1, 0);
    return ids[prevIdx];
  },
);

/**
 * @param state The current state of the store
 * @returns current annotation index (or -1 if none)
 */
export function selectCurrentAnnotationIndex(state: RootState): number {
  const current = state.aiAssetReview.currentAnnotationId;
  if (!current) return -1;
  const annotations = selectAiReviewAnnotations(state);
  return annotations.findIndex((ann) => ann.id === current);
}

/**
 * @param state The current state of the store
 * @returns review statistics
 */
export const selectAiReviewStats = createSelector(
  [selectAiReviewAnnotations],
  (annotations) => {
    const total = annotations.length;
    const approved = annotations.filter(
      (ann) => ann.status === AiAssetReviewStatus.approved,
    ).length;
    const rejected = annotations.filter(
      (ann) => ann.status === AiAssetReviewStatus.rejected,
    ).length;
    const reviewed = approved + rejected;
    const remaining = total - reviewed;
    const progress = total
      ? Math.min(100, Math.max(0, (reviewed / total) * 100))
      : 0;

    return {
      total,
      reviewed,
      remaining,
      approved,
      rejected,
      progress,
    };
  },
);

/**
 * @param state The current state of the store
 * @returns whether navigation to next is possible
 */
export function selectCanNavigateNext(state: RootState): boolean {
  const annotations = selectAiReviewAnnotations(state);
  const currentIndex = selectCurrentAnnotationIndex(state);
  return currentIndex >= 0 && currentIndex < annotations.length - 1;
}

/**
 * @param state The current state of the store
 * @returns whether navigation to previous is possible
 */
export function selectCanNavigatePrevious(state: RootState): boolean {
  const currentIndex = selectCurrentAnnotationIndex(state);
  return currentIndex > 0;
}

/**
 * @param state The current state of the store
 * @returns whether view is step by step
 */
export function selectIsStepByStepView(state: AiAssetReviewStore): boolean {
  return state.aiAssetReview.viewMode === AiReviewViewMode.stepByStep;
}

function deriveStatusFromLabels(markup: MarkupIElement): AiAssetReviewStatus {
  const labels = (markup.labels ?? []).map(
    (l) => l.name.toLowerCase() || l.labelType === LabelType.AiReviewStatus,
  );
  if (labels.includes(AiAssetReviewStatus.approved)) {
    return AiAssetReviewStatus.approved;
  }
  if (labels.includes(AiAssetReviewStatus.rejected)) {
    return AiAssetReviewStatus.rejected;
  }
  return AiAssetReviewStatus.pending;
}

/**  @returns whether to show start asset detection button (no active tasks running) */
export const selectShowStartAssetDetection = createSelector(
  [selectBackgroundTasks(isAssetDetectionTask)],
  (assetDetectionTasks) => {
    const activeTasksCount = assetDetectionTasks.filter(
      (task) =>
        task.state === BackgroundTaskState.started ||
        task.state === BackgroundTaskState.created ||
        task.state === BackgroundTaskState.scheduled,
    ).length;

    const finishedTasksCount = assetDetectionTasks.filter(
      (task) =>
        task.state === BackgroundTaskState.succeeded ||
        task.state === BackgroundTaskState.failed ||
        task.state === BackgroundTaskState.aborted,
    ).length;

    const isRunning =
      activeTasksCount > 0 && finishedTasksCount !== activeTasksCount;
    return !isRunning;
  },
);
